import React, { useState, useEffect } from 'react';
import { Upload, Sparkles, ArrowLeft, Shirt, Smartphone, UtensilsCrossed, Car, Package } from 'lucide-react';
import { API_URL } from '../../config';
import ClothingMarketing from './ClothingMarketing';
import UniversalMarketing from './UniversalMarketing';
import StudioPro from './StudioPro';

export default function MarketingAI({ onBack }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [marketingType, setMarketingType] = useState(null); // 'clothing' ou 'universal'

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients.filter(c => c.status === 'active'));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Si un type est sélectionné, afficher le composant correspondant
  if (marketingType === 'clothing') {
    return (
      <StudioPro
        clientId={selectedClient}
        onBack={() => setMarketingType(null)}
      />
    );
  }

  if (marketingType === 'universal') {
    return (
      <UniversalMarketing 
        clientId={selectedClient}
        onBack={() => setMarketingType(null)}
      />
    );
  }

  // Si un client est sélectionné mais pas de type
  if (selectedClient && !marketingType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
        {/* Header */}
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8">
          <div className="container mx-auto px-4 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/TeckiA-logo.gif" 
                  alt="TeckiA IA" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-base md:text-xl font-bold text-white">TeckiA Marketing</h1>
                  <p className="text-xs md:text-sm text-blue-200">
                    {clients.find(c => c.$id === selectedClient)?.facebook_page_name}
                  </p>
                </div>
              </div>
              <img 
                src="/Teckia-smil.png" 
                alt="TeckiA" 
                className="hidden md:block w-12 h-12 rounded-full object-cover"
              />
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => setSelectedClient(null)}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              🎨 Quel type de produit ?
            </h1>
            <p className="text-xl text-purple-200">
              Choisissez le type de publicité à créer
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Vêtements - Fashn.ai */}
            <button
              onClick={() => setMarketingType('clothing')}
              className="group relative overflow-hidden rounded-3xl p-8 bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-purple-500 transition-all hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-pink-600/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center mb-6 mx-auto group-hover:rotate-12 transition-transform">
                  <Shirt className="w-10 h-10 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-3">Vêtements & Mode</h3>
                <p className="text-white/70 mb-4">
                  Essayage virtuel avec mannequins
                </p>
                
                <div className="space-y-2 text-sm text-white/60">
                  <p>✓ Robes, pantalons, t-shirts</p>
                  <p>✓ Modèles réalistes</p>
                  <p>✓ Try-on virtuel HD</p>
                </div>

                <div className="mt-6 px-4 py-2 rounded-full bg-purple-500/20 text-purple-300 text-sm font-medium inline-block">
                  Powered by TeckiA Fashion
                </div>
              </div>
            </button>

            {/* Produits Universels - Replicate */}
            <button
              onClick={() => setMarketingType('universal')}
              className="group relative overflow-hidden rounded-3xl p-8 bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-cyan-500 transition-all hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center group-hover:rotate-12 transition-transform">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center group-hover:-rotate-12 transition-transform">
                    <UtensilsCrossed className="w-8 h-8 text-white" />
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center group-hover:rotate-12 transition-transform">
                    <Car className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-3">Tous Produits</h3>
                <p className="text-white/70 mb-4">
                  Photos pub avec modèles & produits
                </p>
                
                <div className="space-y-2 text-sm text-white/60">
                  <p>✓ Tech, électronique, gadgets</p>
                  <p>✓ Alimentaire, boissons</p>
                  <p>✓ Voitures, accessoires</p>
                  <p>✓ Mise en scène automatique</p>
                </div>

                <div className="mt-6 px-4 py-2 rounded-full bg-cyan-500/20 text-cyan-300 text-sm font-medium inline-block">
                  Powered by TeckiA Generator
                </div>
              </div>
            </button>
          </div>

          {/* Info Box */}
          <div className="mt-12 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-2xl p-6 border border-white/10">
            <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Comment ça marche ?
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-white/70 text-sm">
              <div>
                <strong className="text-white">Vêtements :</strong> Upload vos modèles et produits, TeckiA génère des photos d'essayage réalistes.
              </div>
              <div>
                <strong className="text-white">Autres produits :</strong> Upload modèles et produits, TeckiA crée des mises en scène publicitaires professionnelles.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Sélection du client
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 md:p-8">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl mb-8">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/TeckiA-logo.gif" 
                alt="TeckiA IA" 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
              />
              <h1 className="text-base md:text-xl font-bold text-white">TeckiA Marketing</h1>
            </div>
            <img 
              src="/Teckia-smil.png" 
              alt="TeckiA" 
              className="hidden md:block w-12 h-12 rounded-full object-cover"
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </button>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          🛍️ TeckiA Fashion Glow
          </h1>
          <p className="text-xl text-purple-200">
            Sélectionnez votre page Facebook
          </p>
        </div>

        <div className="grid gap-4">
          {clients.map(client => (
            <button
              key={client.$id}
              onClick={() => setSelectedClient(client.$id)}
              className="bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-left transition-all hover:scale-105"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Package className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{client.facebook_page_name}</h3>
                  <p className="text-white/60">Cliquez pour continuer</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}